/**
 * Client-side encryption for marked passages.
 *
 * Encryption happens here and nowhere else: the server never sees a passage's
 * plaintext key. A sentence-locked passage is encrypted with its own sentence; a
 * character's or an author-held one with the book's withheld key, which is never
 * published. The ciphertext is encoded as glyphs, for compact and mysterious display.
 *
 * Pure browser implementation — no Node.js dependencies.
 */

import CryptoJS from 'crypto-js'

import { dataToGlyphs, glyphsToData } from './glyphEncoding'

export interface DecryptionResult {
  text: string
  success: boolean
  error?: string
}

// Fixed domain separation salt for PBKDF2 key derivation (no per-message salt)
const KDF_SALT = 'manifesto:v1'
const KDF_ITERATIONS = 100000
const AES_KEY_BITS = 256
const AES_KEY_WORDS = AES_KEY_BITS / 32
const AES_BLOCK_BYTES = 16
const FRAME_VERSION = 1 // single byte

function bytesToWords(u8: Uint8Array): CryptoJS.lib.WordArray {
  const words: number[] = []
  for (let i = 0; i < u8.length; i += 4) {
    words.push(((u8[i] || 0) << 24) | ((u8[i + 1] || 0) << 16) | ((u8[i + 2] || 0) << 8) | (u8[i + 3] || 0))
  }
  return CryptoJS.lib.WordArray.create(words, u8.length)
}

function wordsToBytes(wordArray: CryptoJS.lib.WordArray): Uint8Array {
  const { words, sigBytes } = wordArray
  const u8 = new Uint8Array(sigBytes)
  let idx = 0
  for (let i = 0; i < words.length; i++) {
    const word = words[i]
    const bytesRemaining = sigBytes - idx
    if (bytesRemaining <= 0) {
      break
    }
    const toWrite = Math.min(4, bytesRemaining)
    if (toWrite >= 1) {
      u8[idx++] = (word >> 24) & 0xff
    }
    if (toWrite >= 2) {
      u8[idx++] = (word >> 16) & 0xff
    }
    if (toWrite >= 3) {
      u8[idx++] = (word >> 8) & 0xff
    }
    if (toWrite >= 4) {
      u8[idx++] = word & 0xff
    }
  }
  return u8
}

function bytesToString(bytes: Uint8Array): string {
  let s = ''
  for (let i = 0; i < bytes.length; i++) {
    s += String.fromCharCode(bytes[i])
  }
  return s
}

function stringToBytes(s: string): Uint8Array {
  const out = new Uint8Array(s.length)
  for (let i = 0; i < s.length; i++) {
    out[i] = s.charCodeAt(i) & 0xff
  }
  return out
}

function deriveKey(mnemonic: string): CryptoJS.lib.WordArray {
  return CryptoJS.PBKDF2(mnemonic.trim(), KDF_SALT, {
    keySize: AES_KEY_WORDS,
    iterations: KDF_ITERATIONS,
  })
}

export function encrypt(text: string, key: string): string {
  // Derive key once from mnemonic
  const derivedKey = deriveKey(key)
  // Generate IV and encrypt using explicit key+iv
  const iv = CryptoJS.lib.WordArray.random(AES_BLOCK_BYTES)
  const enc = CryptoJS.AES.encrypt(text, derivedKey, { iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 })
  // Build frame [version][iv][ciphertext]
  const ivBytes = wordsToBytes(iv)
  const ctBytes = wordsToBytes(enc.ciphertext)
  const frame = new Uint8Array(1 + AES_BLOCK_BYTES + ctBytes.length)
  frame[0] = FRAME_VERSION
  frame.set(ivBytes, 1)
  frame.set(ctBytes, 1 + AES_BLOCK_BYTES)
  // Encode to glyphs directly
  return dataToGlyphs(bytesToString(frame))
}

/**
 * Decrypts using 12-word BIP-39 key + encrypted glyphs
 * Key mnemonic should be exactly 12 BIP-39 words
 * Encrypted content is glyphs (not BIP-39 words)
 */
export function decrypt(encrypted: string, key: string): DecryptionResult {
  try {
    if (!encrypted || !key) {
      return {
        text: '',
        success: false,
        error: 'Both encrypted content and key mnemonic are required',
      }
    }

    // Step 1: Convert glyphs back to raw frame bytes
    const frameLatin1 = glyphsToData(encrypted.trim())
    const frame = stringToBytes(frameLatin1)

    // Step 2: Parse frame [version][iv(16)][ciphertext]
    if (frame.length < 1 + AES_BLOCK_BYTES + 1) {
      return {
        text: '',
        success: false,
        error: 'Invalid encrypted data - too short',
      }
    }
    const version = frame[0]
    if (version !== FRAME_VERSION) {
      return {
        text: '',
        success: false,
        error: `Unsupported version ${version}`,
      }
    }
    const ivBytes = frame.slice(1, 1 + AES_BLOCK_BYTES)
    const ctBytes = frame.slice(1 + AES_BLOCK_BYTES)

    // Step 3: Derive AES key from mnemonic
    const derivedKey = deriveKey(key)

    // Step 4: Decrypt using explicit key+iv
    const iv = bytesToWords(ivBytes)
    const ciphertext = bytesToWords(ctBytes)
    const params = CryptoJS.lib.CipherParams.create({ ciphertext })
    const decrypted = CryptoJS.AES.decrypt(params, derivedKey, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    })
    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8)

    if (!decryptedText) {
      return {
        text: '',
        success: false,
        error: 'Decryption failed - invalid key or corrupted data',
      }
    }

    return {
      text: decryptedText,
      success: true,
    }
  } catch (error) {
    return {
      text: '',
      success: false,
      error: error instanceof Error ? error.message : 'Decryption failed',
    }
  }
}
